import { useEffect, useRef, useState } from "react";
import { markDemoSeen } from "./tracking";
import { AnimatePresence, motion } from "framer-motion";
import { BarChart3, Check, CheckCircle2, Download, FileSpreadsheet, FileText, LayoutDashboard, MessageCircle, Pause, Play, Search, Volume2, VolumeX, ShieldCheck, Smartphone, Users, Wallet } from "lucide-react";

const SITE = "myfeemitraapp.in";
const QS = new URLSearchParams(window.location.search);
const clean = (v, n) => (v || "").replace(/[^\p{L}\p{N} .,&'()-]/gu, "").replace(/\s+/g, " ").trim().slice(0, n);
const SCHOOL = clean(QS.get("school"), 50) || "Sunrise Public School";
const CITY = clean(QS.get("city"), 30);
const PLACE = CITY ? `${SCHOOL}, ${CITY}` : SCHOOL;
const INITIALS = SCHOOL.split(" ").filter(Boolean).slice(0, 2).map(w => w[0]).join("").toUpperCase();
const STUDENTS = [
  { id: 1, name: "Aarav Sharma", cls: "Class 5", parent: "Rajesh Sharma", due: 2500, status: "pending" },
  { id: 2, name: "Ananya Verma", cls: "Class 3", parent: "Sunita Verma", due: 0, status: "paid" },
  { id: 3, name: "Kabir Singh", cls: "Class 8", parent: "Harpreet Singh", due: 4200, status: "overdue" },
  { id: 4, name: "Diya Patel", cls: "Class 1", parent: "Mehul Patel", due: 1500, status: "partial" },
  { id: 5, name: "Ishaan Gupta", cls: "Class 10", parent: "Ritu Gupta", due: 0, status: "paid" },
];
const rs = (n) => `₹${n.toLocaleString("en-IN")}`;
const STATUS = { paid: "Paid", pending: "Pending", overdue: "Overdue", partial: "Partial" };

const SCENES = [
  { id: "dashboard", icon: LayoutDashboard, tab: "Dashboard", title: "Poore school ki fee — ek nazar mein", desc: "Aaj kitni fee aayi, kitni baaki hai, kaun se parents online pay kar rahe hain — sab live." },
  { id: "students", icon: Users, tab: "Students", title: "Saare students, ek list mein", desc: "Excel se ek baar mein upload. Naam ya class se turant search karein." },
  { id: "collect", icon: Wallet, tab: "Fee जमा", title: "Fee jama karna — sirf 1 click", desc: "Cash, cheque, UPI ya card — har payment ka record apne aap." },
  { id: "receipt", icon: FileText, tab: "Receipt", title: "Payment hote hi PDF receipt", desc: "School ke naam aur logo ke saath. Parent ko WhatsApp par bhi jaati hai." },
  { id: "whatsapp", icon: MessageCircle, tab: "WhatsApp", title: "WhatsApp par fee reminder", desc: "Baaki fee wale parents ko ek click mein reminder aur pay link." },
  { id: "parent", icon: Smartphone, tab: "Parent App", title: "Parents ghar baithe fee bharein", desc: "Bina app download kiye — Google login, UPI / Card se payment." },
  { id: "reports", icon: BarChart3, tab: "Reports", title: "Reports — Excel mein download", desc: "Class-wise collection, baaki fee, daily cash book — sab tayyar." },
  { id: "end", icon: CheckCircle2, tab: "Free Trial", title: "", desc: "" },
];

function Stat({ label, value, tone, i }) {
  return <motion.div className={`ld-stat ${tone}`} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 + i * 0.08 }}><span>{label}</span><strong>{value}</strong></motion.div>;
}

function Dashboard() {
  const bars = [42, 58, 51, 76, 69, 88];
  return <div className="ld-pad" data-testid="ld-scene-dashboard">
    <div className="ld-stats">{[["Aaj ki collection", "₹48,500", "green"], ["Is mahine", "₹6,82,000", "blue"], ["Baaki fee", "₹1,24,300", "amber"], ["Online payments", "64%", "slate"]].map((s, i) => <Stat key={s[0]} label={s[0]} value={s[1]} tone={s[2]} i={i} />)}</div>
    <div className="ld-chart"><b>Monthly collection</b><div className="ld-bars">{bars.map((h, i) => <div key={i} className="ld-bar-wrap"><motion.div className="ld-bar" initial={{ height: 0 }} animate={{ height: `${h}%` }} transition={{ delay: 0.3 + i * 0.08, duration: 0.5 }} /><small>{["Jan", "Feb", "Mar", "Apr", "May", "Jun"][i]}</small></div>)}</div></div>
  </div>;
}

function Students() {
  const [q, setQ] = useState("");
  const list = STUDENTS.filter(s => `${s.name} ${s.cls}`.toLowerCase().includes(q.toLowerCase()));
  return <div className="ld-pad" data-testid="ld-scene-students">
    <div className="ld-toolbar"><label className="ld-search"><Search size={14} /><input value={q} onChange={e => setQ(e.target.value)} placeholder="Naam ya class search karein…" data-testid="ld-student-search" /></label><span className="ld-chip"><FileSpreadsheet size={13} /> Excel upload</span></div>
    <div className="ld-rows">{list.map((s, i) => <motion.div key={s.id} className="ld-row" initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.07 }}>
      <span className="ld-av">{s.name.split(" ").map(w => w[0]).join("")}</span><div><b>{s.name}</b><small>{s.cls} · {s.parent}</small></div>
      <em className={`ld-st ${s.status}`}>{STATUS[s.status]}</em><strong>{s.due ? rs(s.due) : "—"}</strong></motion.div>)}</div>
  </div>;
}

function Collect({ onPaid, mode, setMode }) {
  return <div className="ld-pad ld-center" data-testid="ld-scene-collect">
    <div className="ld-card">
      <small>Fee collect karein</small><h4>Aarav Sharma · Class 5</h4>
      <div className="ld-total"><span>Baaki fee (July)</span><strong>{rs(2500)}</strong></div>
      <div className="ld-modes">{["Cash", "UPI", "Card", "Cheque"].map(m => <button key={m} className={mode === m ? "on" : ""} onClick={() => setMode(m)} data-testid={`ld-mode-${m.toLowerCase()}`}>{m}</button>)}</div>
      <button className="ld-primary" onClick={onPaid} data-testid="ld-collect-btn"><Check size={16} /> {rs(2500)} jama karein ({mode})</button>
    </div>
  </div>;
}

function Receipt({ mode }) {
  return <div className="ld-pad ld-center" data-testid="ld-scene-receipt">
    <motion.div className="ld-card ld-receipt" initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
      <div className="ld-rhead"><div><small>FEE RECEIPT</small><h4 data-testid="ld-receipt-school">{SCHOOL}</h4>{CITY && <small>{CITY}</small>}</div><CheckCircle2 size={30} color="#059669" /></div>
      <div className="ld-rgrid"><div><small>Receipt No</small><b>SPS/2026/0142</b></div><div><small>Date</small><b>10 Jul 2026</b></div><div><small>Student</small><b>Aarav Sharma</b></div><div><small>Mode</small><b>{mode}</b></div></div>
      <div className="ld-ramt"><span>Amount paid</span><strong>{rs(2500)}</strong></div>
      <span className="ld-chip"><Download size={13} /> PDF download</span>
    </motion.div>
  </div>;
}

function WhatsApp() {
  const [sent, setSent] = useState(false);
  useEffect(() => { const t = setTimeout(() => setSent(true), 1400); return () => clearTimeout(t); }, []);
  return <div className="ld-pad ld-center" data-testid="ld-scene-whatsapp">
    <div className="ld-wa">
      <div className="ld-wa-head"><span className="ld-av">{INITIALS}</span><div><b>{SCHOOL}</b><small>via FeeMitra</small></div></div>
      <div className="ld-wa-body">
        <AnimatePresence>{sent && <motion.div className="ld-bubble" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} data-testid="ld-wa-message">
          Namaste Kabir Singh ke parent 🙏<br />Class 8 ki fee <b>{rs(4200)}</b> baaki hai.<br />Yahan se turant bharein: <u>{SITE}/pay/k8x2</u><br /><small>— {SCHOOL}</small></motion.div>}</AnimatePresence>
      </div>
      <button className="ld-wa-btn" onClick={() => setSent(true)} data-testid="ld-wa-send"><MessageCircle size={15} /> {sent ? "Reminder bhej diya ✓" : "WhatsApp reminder bhejein"}</button>
    </div>
  </div>;
}

function Parent() {
  const [paid, setPaid] = useState(false);
  return <div className="ld-pad ld-center" data-testid="ld-scene-parent">
    <div className="ld-phone">
      <div className="ld-phone-top"><ShieldCheck size={14} /> {SCHOOL}</div>
      <p className="ld-hi">Namaste, Rajesh ji</p>
      <div className="ld-child"><b>Aarav Sharma</b><small>Class 5</small><strong>{paid ? "Fee paid ✓" : `${rs(2500)} baaki`}</strong></div>
      <div className="ld-upi">{["PhonePe", "GPay", "Paytm", "Card"].map(u => <span key={u}>{u}</span>)}</div>
      <button className="ld-primary" onClick={() => setPaid(true)} data-testid="ld-parent-pay">{paid ? <><CheckCircle2 size={16} /> Payment safal!</> : <>Abhi {rs(2500)} bharein</>}</button>
    </div>
  </div>;
}

function Reports() {
  const rows = [["Class-wise collection", "Har class ki jama aur baaki fee"], ["Pending fee list", "Kis bachche ki kitni fee baaki"], ["Daily cash book", "Aaj ki saari entries"], ["Parent usage", "Kaun se parents online hain"]];
  return <div className="ld-pad" data-testid="ld-scene-reports"><div className="ld-reports">{rows.map(([t, d], i) =>
    <motion.div key={t} className="ld-report" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}><FileSpreadsheet size={20} /><b>{t}</b><small>{d}</small><span className="ld-chip"><Download size={13} /> Excel</span></motion.div>)}</div></div>;
}

function EndScreen() {
  return <motion.div className="ld-end" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} data-testid="ld-scene-end">
    <ShieldCheck size={44} />
    {QS.get("school") && <p className="ld-for" data-testid="ld-end-for">{PLACE} ke liye</p>}
    <h2>14 दिन Free Trial</h2>
    <p>Koi card nahi · 2 minute mein setup · Hindi + English</p>
    <div className="ld-url">{SITE}</div>
    <a className="ld-primary ld-cta" href="/free-trial?utm_source=demo_video&utm_campaign=live_demo" data-testid="ld-end-cta">Free Trial शुरू करें</a>
  </motion.div>;
}

function Stage({ scene, go, mode, setMode }) {
  const body = { dashboard: <Dashboard />, students: <Students />, collect: <Collect onPaid={() => go(3)} mode={mode} setMode={setMode} />, receipt: <Receipt mode={mode} />, whatsapp: <WhatsApp />, parent: <Parent />, reports: <Reports /> }[scene.id];
  if (scene.id === "end") return <EndScreen />;
  return <div className="ld-stage">
    <motion.div className="ld-caption" key={`c-${scene.id}`} initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }}><span className="ld-kicker"><scene.icon size={14} /> {scene.tab}</span><h2 data-testid="ld-scene-title">{scene.title}</h2><p>{scene.desc}</p></motion.div>
    <motion.div className="ld-screen" key={`s-${scene.id}`} initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
      <div className="ld-screen-bar"><i /><i /><i /><span data-testid="ld-screen-school">{PLACE} · FeeMitra</span></div>{body}</motion.div>
  </div>;
}

function useVoice(sceneId, active, muted) {
  const ref = useRef(null);
  useEffect(() => {
    ref.current?.pause();
    if (!active || muted) return;
    const a = new Audio(`/demo/vo-${sceneId}.mp3`); ref.current = a;
    a.play().catch(() => {});
    return () => a.pause();
  }, [sceneId, active, muted]);
}

function DemoHeader({ playing, toggle, muted, setMuted }) {
  return <header className="ld-nav"><a href="/free-trial" className="ld-brand"><ShieldCheck size={20} /> FeeMitra</a><span className="ld-badge" data-testid="ld-sample-badge"><span className="ld-badge-long">LIVE DEMO · </span>नमूना data</span>
    <button className="ld-mute" onClick={() => setMuted(m => !m)} aria-label="Awaaz" data-testid="ld-mute-btn">{muted ? <VolumeX size={16} /> : <Volume2 size={16} />}</button>
    <button className="ld-play" onClick={toggle} data-testid="ld-autoplay-btn">{playing ? <><Pause size={15} /> Pause</> : <><Play size={15} /> Auto Play</>}</button></header>;
}

export default function LiveDemoPage() {
  const params = new URLSearchParams(window.location.search);
  const record = params.has("record");
  const vertical = params.has("vertical");
  const [idx, setIdx] = useState(0);
  const [mode, setMode] = useState("UPI");
  const [muted, setMuted] = useState(params.has("mute"));
  const [playing, setPlaying] = useState(params.has("autoplay"));
  const last = SCENES.length - 1;
  useVoice(SCENES[idx].id, playing || idx === last, muted);
  useEffect(() => { document.title = "FeeMitra Live Demo"; if (!record) markDemoSeen(); }, [record]);
  useEffect(() => { if (!record) return; const t = setTimeout(() => { window.__ldStartedAt = Date.now(); setPlaying(true); }, 1200); return () => clearTimeout(t); }, [record]);
  useEffect(() => {
    if (!playing) return;
    if (idx >= last) { setPlaying(false); return; }
    const t = setTimeout(() => setIdx(i => i + 1), 5000);
    return () => clearTimeout(t);
  }, [playing, idx, last]);
  const toggle = () => { if (!playing && idx >= last) setIdx(0); setPlaying(p => !p); };
  const go = (i) => { setPlaying(false); setIdx(i); };
  return <div className={`ld${record ? " ld-record" : ""}${vertical ? " ld-vertical" : ""}`} data-testid="live-demo-page">
    {record ? <div className="ld-watermark" data-testid="ld-watermark"><ShieldCheck size={18} /> FeeMitra</div> : <>
      <DemoHeader playing={playing} toggle={toggle} muted={muted} setMuted={setMuted} />
      <div className="ld-progress"><motion.div key={`${idx}-${playing}`} initial={{ width: playing ? "0%" : "100%" }} animate={{ width: "100%" }} transition={{ duration: playing ? 5 : 0, ease: "linear" }} /></div>
      <nav className="ld-tabs" data-testid="ld-tabs">{SCENES.map((s, i) => <button key={s.id} className={i === idx ? "on" : ""} ref={i === idx ? (el) => el?.scrollIntoView({ block: "nearest", inline: "center", behavior: "smooth" }) : null} onClick={() => go(i)} data-testid={`ld-tab-${s.id}`}><s.icon size={14} /> {s.tab}</button>)}</nav></>}
    <main className="ld-main"><AnimatePresence mode="wait"><motion.div key={SCENES[idx].id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}><Stage scene={SCENES[idx]} go={go} mode={mode} setMode={setMode} /></motion.div></AnimatePresence></main>
  </div>;
}
